import { useImportTableDefinition } from '@front/hook/Api/ImportTableDefinition';
import { useNavigateBack } from '@front/hook/useNavigateBack';
import type { IImportTableDefinitionRepository } from '@front/repository/Api/ImportTableDefinition/interface';
import { useToast } from '@plaidev/sour-react';
import type { UseMutationResult } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useAccount, useProject } from 'auth-lib-front';
import type { BaseSyntheticEvent } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import { DEFAULT_CUSTOMER_IDENTIFIER_TYPE, DEFAULT_VALUES } from './constants';
import type { ColumnEditorContextValue } from './context/ColumnEditorContext';
import { type UseAnalyzeFlowReturn, useAnalyzeFlow } from './hooks/useAnalyzeFlow';
import { useColumnDraftActions } from './hooks/useColumnDraftActions';
import { useColumnFilter } from './hooks/useColumnFilter';
import { useColumnSelection } from './hooks/useColumnSelection';
import { useSourceSystemSelection } from './hooks/useSourceSystemSelection';
import { validateCreateTableDefinitionSubmit } from './submitValidation';
import type { FormValues } from './types';

type CreateMutation = UseMutationResult<
  Awaited<ReturnType<IImportTableDefinitionRepository['create']>>,
  Error,
  IImportTableDefinitionRepository.Create.Params
>;

export type UseTableDefinitionNewPageReturn = {
  methods: UseFormReturn<FormValues>;
  onSubmit: (e?: BaseSyntheticEvent) => void | Promise<void>;
  sourcesPending: boolean;
  isSystemAdmin: boolean;
  navigateToList: () => void;
  createMutation: CreateMutation;
  analyzeFlow: UseAnalyzeFlowReturn;
  sourceSystem: ReturnType<typeof useSourceSystemSelection>;
  sourceSystemId: string | undefined;
  columnEditor: ColumnEditorContextValue;
};

const routeApi = getRouteApi('/table-definitions/new');

export function useTableDefinitionNewPage(): UseTableDefinitionNewPageReturn {
  const navigate = useNavigate();
  const goBackWithFallback = useNavigateBack();
  const toast = useToast();
  const { isSystemAdmin } = useAccount();
  const { project } = useProject();
  const { sourceSystemId: initialSourceSystemId } = routeApi.useSearch();

  const createMutation = useImportTableDefinition.Create();

  const methods = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });
  const { control, handleSubmit, reset, setValue, getValues } = methods;

  const sourceSystemId = useWatch({ control, name: 'sourceSystemId' });
  const columns = useWatch({ control, name: 'columns' });
  const customerUkEnabled = useWatch({ control, name: 'customerUkEnabled' });

  const navigateToList = () => goBackWithFallback({ to: '/table-definitions' });

  const analyzeFlow = useAnalyzeFlow({
    projectId: project.id,
    isSystemAdmin,
    getValues,
    reset,
    navigateToList,
  });

  const sourceSystem = useSourceSystemSelection({
    projectId: project.id,
    initialSourceSystemId,
    sourceSystemId,
    setValue,
  });

  const columnFilter = useColumnFilter({ columns });
  const selection = useColumnSelection({
    columns,
    filteredColumns: columnFilter.filteredColumns,
  });
  const columnActions = useColumnDraftActions({
    getValues,
    setValue,
    selectedNames: selection.selectedNames,
  });

  const columnEditor: ColumnEditorContextValue = {
    counts: columnFilter.counts,
    searchText: columnFilter.searchText,
    setSearchText: columnFilter.setSearchText,
    destinationFilter: columnFilter.destinationFilter,
    setDestinationFilter: columnFilter.setDestinationFilter,
    filteredColumns: columnFilter.filteredColumns,
    filteredAllSelected: selection.filteredAllSelected,
    selectedNames: selection.selectedNames,
    toggleRowSelection: selection.toggleRowSelection,
    toggleSelectAllFiltered: selection.toggleSelectAllFiltered,
    clearColumnSelection: selection.clearColumnSelection,
    updateColumn: columnActions.updateColumn,
    bulkSetDestination: columnActions.bulkSetDestination,
    contractColumnNames: columnFilter.contractColumnNames,
    customerColumnNames: columnFilter.customerColumnNames,
    customerUkEnabled,
  };

  const onSubmit = handleSubmit(
    async (values) => {
      const validationError = validateCreateTableDefinitionSubmit(values, analyzeFlow.analysis);
      if (validationError) {
        toast.error(validationError);
        return;
      }
      if (!analyzeFlow.analysis) return;

      const trimmedName = values.displayName.trim();
      const trimmedDescription = values.description.trim();
      try {
        const created = await createMutation.mutateAsync({
          projectId: project.id,
          fileId: analyzeFlow.uploadedFileId ?? undefined,
          createProps: {
            sourceSystemId: values.sourceSystemId,
            displayName: trimmedName,
            description: trimmedDescription === '' ? undefined : trimmedDescription,
            schemaJson: {
              hasHeaderRow: analyzeFlow.analysis.hasHeaderRow,
              encoding: analyzeFlow.analysis.detectedEncoding,
              delimiter: analyzeFlow.analysis.detectedDelimiter,
              columns: values.columns.map((d) => ({
                name: d.name,
                type: d.type,
                required: d.required,
                description: undefined,
                destination: d.destination,
                transformRules: d.transformRules,
                ...(d.multiValued && d.destination === 'customer' ? { multiValued: true } : {}),
              })),
            },
            uniqueKeys: {
              contract: { columnNames: values.contractUkColumns },
              customer:
                values.customerUkEnabled && values.customerUkColumns.length > 0
                  ? {
                      columnNames: values.customerUkColumns,
                      identifierType: DEFAULT_CUSTOMER_IDENTIFIER_TYPE,
                    }
                  : undefined,
            },
            loadMode: values.loadMode,
            importMethod: 'Upload',
            isActive: true,
          },
        });
        toast.success(analyzeFlow.uploadedFileId ? 'テーブル定義を作成し、取り込みタスクを起動しました' : 'テーブル定義を作成しました');
        if (created.task) {
          void navigate({ to: '/tasks/$id', params: { id: created.task.id } });
        } else {
          void navigate({ to: '/table-definitions/$id', params: { id: created.detail.definition.id } });
        }
      } catch (err) {
        console.error('[table-definitions/new] create failed', err);
        // タイムアウト (AbortError) ではサーバ側でコミット済みかもしれないため、
        // 同画面で再 submit させると二重生成のリスクがある。一覧へ遷移して結果を確認させる。
        // それ以外（バリデーションエラー等）は二重生成リスクが無く、ユーザーが入力修正して
        // 再試行できるよう、サーバが返したメッセージを出してその場に留める。
        if (err instanceof Error && err.name === 'AbortError') {
          toast.error('作成リクエストが完了しませんでした。一覧で結果をご確認ください。');
          navigateToList();
        } else {
          const message = err instanceof Error ? err.message : '作成に失敗しました';
          toast.error(message);
        }
      }
    },
    (errors) => {
      console.warn('[table-definitions/new] validation errors', errors);
      const first = Object.values(errors)[0] as { message?: string } | undefined;
      toast.error(first?.message || '入力内容に不備があります');
    },
  );

  return {
    methods,
    onSubmit,
    sourcesPending: sourceSystem.sourcesPending,
    isSystemAdmin,
    navigateToList,
    createMutation,
    analyzeFlow,
    sourceSystem,
    sourceSystemId,
    columnEditor,
  };
}
